import React, { useState } from 'react'
import { connect } from 'react-redux'
import { httpClient } from '../../funcs'
import { notifyAboutError } from '../../methods/notifyUser'
import trashIcon from '../../images/pages/tariffsV2/trash.svg'
import RequestBillPopupV2 from '../Profile/RequestBillPopupV2'
import OrderInfo from './OrderInfo'
import { isInstallmentPackage } from '../../util/installment'
import 'pages/Styles/Cart.sass'
import 'pages/Styles/UIKit_v2.sass'

type Props = {
    cart: any[]
    onRemoveItem: (itemId: string, itemType: string) => void
    /** Удалить апгрейд проверки подписанта из пакета `package_v2` */
    onRemovePackageUpgrade?: (upgradeId: string) => void
    onUpdateQuantity: (itemId: string, itemType: string, quantity: number) => void
    onClearCart: () => void
    onBillCreated?: () => void  // Callback для сброса корзины после создания счета
    onPaymentProofUploaded?: (items?: any[]) => void  // Callback после загрузки платежного поручения
    onPaymentRequest?: (method: 'card' | 'invoice' | 'sbp') => Promise<boolean> | boolean  // Callback для внешней логики перед оплатой
    onLoadTariffs?: () => void  // Callback для обновления тарифов
    promoCodes?: any
    /** Показывать кнопку «Оплатить через СБП» (флаг из конфига, передаётся с GET /tariffs-v2) */
    showSbpButton?: boolean
    $store: any
    $commitToStore: (data: any) => void
}

type ForeignCurrency = 'KZT' | 'BYN'

/** Код юрисдикции из профиля (`userSession.jurisdiction`), см. Profile.tsx */
function jurisdictionFromProfile(userSession?: { jurisdiction?: string | null } | null): 'KZ' | 'BY' | 'RU' | 'AM' {
    const raw = (userSession?.jurisdiction || 'RU').toString().toUpperCase()
    if (raw === 'KZ' || raw === 'BY' || raw === 'RU' || raw === 'AM') {
        return raw
    }
    return 'RU'
}

let Cart: any = function Cart({ cart, onRemoveItem, onRemovePackageUpgrade, onUpdateQuantity, onClearCart, onBillCreated, onPaymentProofUploaded, onPaymentRequest, onLoadTariffs, promoCodes, showSbpButton = false, $store, $commitToStore }: Props) {
    const [showBillPopup, setShowBillPopup] = useState(false)
    const [foreignRequest, setForeignRequest] = useState<{ loading: boolean; sent: boolean; currency: ForeignCurrency | null }>({
        loading: false,
        sent: false,
        currency: null,
    })

    const jurisdiction = jurisdictionFromProfile($store?.userSession)
    const foreignCurrency: ForeignCurrency | null =
        jurisdiction === 'KZ' ? 'KZT' :
        jurisdiction === 'BY' ? 'BYN' :
        null

    function getSelectedPackage() {
        return cart.find(item => 
            item.type === 'package_v2'
        )
    }

    function handleBillCreated() {
        console.log('handleBillCreated', cart)
        if (onBillCreated) {
            onBillCreated()
        }
    }

    function handlePaymentProofUploaded() {
        // Закрываем попап и вызываем callback для сброса корзины после загрузки платежного поручения
        console.log('handlePaymentProofUploaded', cart)
        setShowBillPopup(false)
        if (onPaymentProofUploaded) {
            onPaymentProofUploaded(cart)
        }
    }

    async function handlePayment(method: 'card' | 'invoice' | 'sbp') {
        try {
            if (onPaymentRequest) {
                const handledExternally = await onPaymentRequest(method)
                if (handledExternally) {
                    return
                }
            }

            if (method === 'card' || method === 'sbp') {
                // Оплата картой или через СБП — бэкенд инициирует платёж (Init с connection_type=Widget для СБП) и возвращает PaymentURL
                const { data: result } = await httpClient.post('/buy-cart', {
                    cart: cart,
                    payment_method: method === 'sbp' ? 'sbp' : undefined
                })

                const paymentUrl = result.PaymentURL ?? result.paymentUrl
                if (paymentUrl) {
                    window.location.href = paymentUrl
                } else {
                    notifyAboutError({ $store, $commitToStore }, new Error('Не удалось создать платеж'))
                }
            } else if (method === 'invoice') {
                // Для заказа счета открываем попап
                setShowBillPopup(true)
            }
        } catch (error) {
            notifyAboutError({ $store, $commitToStore }, error)
        }
    }

    async function handleForeignPaymentLinkRequest() {
        if (!foreignCurrency) return
        if (foreignRequest.loading || foreignRequest.sent) return
        try {
            setForeignRequest({ loading: true, sent: false, currency: foreignCurrency })
            await httpClient.post('/request-payment-link', {
                cart: cart,
                currency: foreignCurrency,
            })
            setForeignRequest({ loading: false, sent: true, currency: foreignCurrency })
        } catch (error) {
            setForeignRequest({ loading: false, sent: false, currency: null })
            notifyAboutError({ $store, $commitToStore }, error)
        }
    }

    function renderBillPopup() {
        return (
            <RequestBillPopupV2
                close={() => {
                    if (onClearCart) {
                        onClearCart()
                    }
                    setShowBillPopup(false)
                }}
                onCollectBonus={() => {}}
                onBillCreated={handleBillCreated}
                onLoadTariffs={onLoadTariffs}
                onPaymentProofUploaded={handlePaymentProofUploaded}
                $store={$store}
                $commitToStore={$commitToStore}
                items={cart}
            />
        )
    }

    function renderEmptyCart() {
        return (
            <div className="cart-v2">
                <div className="empty-cart">
                    <div className="empty-cart-text">В корзине пока пусто</div>
                </div>
            </div>
        )
    }

    function renderRubPaymentButtons() {
        return (
            <>
                <button
                    className="new action-button secondary"
                    onClick={() => handlePayment('invoice')}
                >
                    <span className="button-text">Заказать счет</span>
                </button>
                {showSbpButton && !cart.some(isInstallmentPackage) && (
                    <button
                        className="new action-button sbp"
                        onClick={() => handlePayment('sbp')}
                    >
                        <span className="button-text">Оплатить через СБП</span>
                    </button>
                )}
                <button
                    className="new action-button primary"
                    onClick={() => handlePayment('card')}
                >
                    <span className="button-text">Оплатить картой</span>
                </button>
            </>
        )
    }

    function renderForeignPaymentSection() {
        if (!foreignCurrency) return null

        const currencyTitle = foreignCurrency === 'KZT'
            ? 'Оплата в тенге (KZT)'
            : 'Оплата в белорусских рублях (BYN)'

        const currencyHint = foreignCurrency === 'KZT'
            ? 'Для клиентов из Казахстана'
            : 'Для клиентов из Беларуси'

        return (
            <div className="cart-payment-section cart-payment-section--foreign">
                <div className="cart-payment-section-title">{currencyTitle}</div>
                <div className="cart-payment-section-hint">{currencyHint}</div>
                {foreignRequest.sent ? (
                    <div className="cart-payment-section-success">
                        Запрос отправлен. С вами свяжется менеджер для выставления счёта в валюте.
                    </div>
                ) : (
                    <button
                        className="new action-button primary"
                        onClick={handleForeignPaymentLinkRequest}
                        disabled={foreignRequest.loading}
                    >
                        <span className="button-text">
                            {foreignRequest.loading ? 'Отправляем запрос…' : 'Запросить платёжную ссылку'}
                        </span>
                    </button>
                )}
            </div>
        )
    }

    function renderCart() {
        return (<div className="cart-v2">
            <div className="titleContainer">
                <div className="cart-package-title">
                    {selectedPackage ? selectedPackage.name : 'Дополнительные услуги'}
                </div>
                <button 
                    className="new iconed dark"
                    onClick={onClearCart}
                >
                    <img src={trashIcon} alt="Удалить" />
                </button>
            </div>

            <OrderInfo
                showTitle={false}
                cart={cart}
                promoCodes={promoCodes}
                onRemoveItem={onRemoveItem}
                onRemovePackageUpgrade={onRemovePackageUpgrade}
            />

                <div className="cart-actions">
                    {foreignCurrency ? (
                        <>
                            <div className="cart-payment-section cart-payment-section--rub">
                                <div className="cart-payment-section-title">Оплата в рублях (RUB)</div>
                                {renderRubPaymentButtons()}
                            </div>
                            {renderForeignPaymentSection()}
                        </>
                    ) : (
                        renderRubPaymentButtons()
                    )}
                </div>
        </div>
    )
    }

    const selectedPackage = getSelectedPackage()

    return (
        <>
        { cart.length === 0 && renderEmptyCart() }
        { cart.length > 0 && renderCart() } 
        {showBillPopup && renderBillPopup() }
        </>
    )
}

Cart = connect(
    (store) => ({ $store: store }),
    (dispatch) => ({
        $commitToStore: (data: any) => dispatch({ ...data, type: 'S' })
    })
)(Cart)

export default Cart
